// StoryEditor.js — Updated to use authFetch
import React, { useState } from 'react';

export default function StoryEditor({ token, authFetch, stories, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');

  const storyList = Array.isArray(stories) ? stories : [];

  const emptyStory = {
    title: '',
    content: '',
    excerpt: '',
    tags: '',
    status: 'draft',
  };

  async function saveStory(storyData) {
    try {
      const isNew = !storyData.id;
      const url = isNew ? '/api/stories' : `/api/stories/${storyData.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const tags = typeof storyData.tags === 'string'
        ? storyData.tags.split(',').map(t => t.trim()).filter(Boolean)
        : storyData.tags || [];

      const payload = {
        title: storyData.title,
        content: storyData.content,
        excerpt: storyData.excerpt,
        tags,
        status: storyData.status,
      };

      if (storyData.id) {
        payload.id = storyData.id;
      }

      console.log(`[ADMIN] ${method} ${url}`, { status: payload.status });

      // Use authFetch if available, fall back to manual fetch
      let res;
      if (authFetch) {
        res = await authFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        setEditing(null);
        setMessage(isNew ? 'Transcript archived!' : 'Transcript updated!');
        setTimeout(() => setMessage(''), 3000);
        if (onRefresh) onRefresh();
      } else {
        const err = await res.json();
        setMessage(`Error: ${err.error || 'Failed to save'}`);
      }
    } catch (err) {
      console.error('[ADMIN] Save error:', err);
      setMessage('Error connecting to server');
    }
  }

  async function deleteStory(id) {
    if (!window.confirm('Delete this transcript? This cannot be undone.')) return;

    try {
      let res;
      if (authFetch) {
        res = await authFetch(`/api/stories/${id}`, { method: 'DELETE' });
      } else {
        res = await fetch(`/api/stories/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }

      if (res.ok) {
        setMessage('Transcript deleted');
        setTimeout(() => setMessage(''), 3000);
        if (onRefresh) onRefresh();
      } else {
        setMessage('Failed to delete transcript');
      }
    } catch (err) {
      setMessage('Error connecting to server');
    }
  }

  async function togglePublished(story) {
    const newStatus = story.status === 'published' ? 'draft' : 'published';
    await saveStory({ ...story, status: newStatus });
  }

  function isPublished(story) {
    return story.status === 'published';
  }

  if (editing) {
    return (
      <StoryForm
        story={editing}
        onSave={saveStory}
        onCancel={() => setEditing(null)}
        message={message}
      />
    );
  }

  return (
    <div>
      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${message.includes('Error') ? '#ff0040' : '#00ff41'}`,
          color: message.includes('Error') ? '#ff0040' : '#00ff41',
        }}>
          {message}
        </div>
      )}

      <button
        className="bbs-button"
        onClick={() => setEditing({ ...emptyStory })}
        style={{ marginBottom: '20px' }}
      >
        + NEW TRANSCRIPT
      </button>

      {storyList.length === 0 ? (
        <div style={{ color: '#555', padding: '20px' }}>
          No transcripts yet. Click "+ NEW TRANSCRIPT" to create one.
        </div>
      ) : (
        <div>
          {storyList.map(story => {
            const published = isPublished(story);
            return (
              <div
                key={story.id || story._id}
                style={{
                  border: '1px solid #333',
                  padding: '12px 15px',
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: published ? 1 : 0.5,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#00e5ff', fontWeight: 'bold' }}>
                      {story.title}
                    </span>
                    <span style={{
                      color: published ? '#00ff41' : '#ffb000',
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      border: `1px solid ${published ? '#00ff41' : '#ffb000'}`,
                    }}>
                      {published ? 'LIVE' : 'DRAFT'}
                    </span>
                    {story.views > 0 && (
                      <span style={{ color: '#555', fontSize: '0.7rem' }}>
                        ({story.views} views)
                      </span>
                    )}
                  </div>
                  {story.excerpt && (
                    <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '4px' }}>
                      {story.excerpt.substring(0, 100)}...
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                  <button
                    className="bbs-button"
                    onClick={() => togglePublished(story)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      borderColor: published ? '#ffb000' : '#00ff41',
                      color: published ? '#ffb000' : '#00ff41',
                    }}
                  >
                    {published ? 'UNPUBLISH' : 'PUBLISH'}
                  </button>
                  <button
                    className="bbs-button"
                    onClick={() => setEditing({
                      ...story,
                      tags: Array.isArray(story.tags) ? story.tags.join(', ') : (story.tags || ''),
                    })}
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                  >
                    EDIT
                  </button>
                  <button
                    className="bbs-button"
                    onClick={() => deleteStory(story.id || story._id)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      borderColor: '#ff0040',
                      color: '#ff0040',
                    }}
                  >
                    DEL
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StoryForm({ story, onSave, onCancel, message }) {
  const [form, setForm] = useState({
    id: story.id || story._id || null,
    title: story.title || '',
    content: story.content || '',
    excerpt: story.excerpt || '',
    tags: story.tags || '',
    status: story.status || 'draft',
  });

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('Title is required');
      return;
    }
    if (!form.content.trim()) {
      alert('Content is required');
      return;
    }
    onSave(form);
  }

  const isPublished = form.status === 'published';

  return (
    <form onSubmit={handleSubmit}>
      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${message.includes('Error') ? '#ff0040' : '#00ff41'}`,
          color: message.includes('Error') ? '#ff0040' : '#00ff41',
        }}>
          {message}
        </div>
      )}

      <h3 style={{ color: '#00e5ff', marginBottom: '20px' }}>
        {form.id ? 'EDIT TRANSCRIPT' : 'NEW TRANSCRIPT'}
      </h3>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          TITLE *
        </label>
        <input
          type="text"
          value={form.title}
          onChange={e => handleChange('title', e.target.value)}
          className="admin-input"
          placeholder="Transcript title"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          EXCERPT
        </label>
        <input
          type="text"
          value={form.excerpt}
          onChange={e => handleChange('excerpt', e.target.value)}
          className="admin-input"
          placeholder="Brief description shown in archive list"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          CONTENT *
        </label>
        <textarea
          value={form.content}
          onChange={e => handleChange('content', e.target.value)}
          className="admin-input"
          placeholder="Transcript content... (Markdown supported)"
          rows={20}
          style={{
            width: '100%',
            resize: 'vertical',
            minHeight: '300px',
            lineHeight: '1.6',
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          TAGS (comma separated)
        </label>
        <input
          type="text"
          value={form.tags}
          onChange={e => handleChange('tags', e.target.value)}
          className="admin-input"
          placeholder="signal, ophidia, classified"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          color: isPublished ? '#00ff41' : '#ffb000',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <input
            type="checkbox"
            checked={isPublished}
            onChange={e => handleChange('status', e.target.checked ? 'published' : 'draft')}
          />
          {isPublished ? 'PUBLISHED — Visible in public archive' : 'DRAFT — Hidden from public archive'}
        </label>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="submit" className="bbs-button">
          {form.id ? 'SAVE CHANGES' : 'ARCHIVE TRANSCRIPT'}
        </button>
        <button
          type="button"
          className="bbs-button"
          onClick={onCancel}
          style={{ borderColor: '#555', color: '#555' }}
        >
          CANCEL
        </button>
      </div>
    </form>
  );
}